import { sum } from 'lib/array/arrayUtils'
import { FlightViewTypes } from 'constants/flight'
import { sumOtherFees } from 'checkout/lib/utils/getItemViewTotalOtherFees'
import { computeBaggageCostForPaxList } from 'checkout/lib/utils/flights/extras'
import { baggageOptionsMap } from 'api/lib/convertJourneyV2'

type JourneysByFlightJourneyId = Record<string, App.JourneyV2 | undefined>

/**
 * Mirrors the price logic in mapFlightV2ToView so cart evaluation, checkout display,
 * and order payload journey.cost stay aligned after final fare fetch (totalFare vs per-leg costs).
 */
export function getFlightItemViewPrice(
  item: App.Checkout.FlightItem,
  journeysByFlightJourneyId: JourneysByFlightJourneyId,
): number {
  const fares = sum(item.flights.map(flight => flight.fareFamily?.cost?.all ?? flight.cost ?? 0))
  const journeysFareTypes = item.flights.map(
    flight => journeysByFlightJourneyId[flight.journeyId]?.fareType,
  )

  const totalFare = item.totalFare ?? 0
  const isReturnFares = journeysFareTypes.includes(FlightViewTypes.RETURN)
  let price = totalFare > 0 ? totalFare : fares

  const departing = item.flights[0]
    ? journeysByFlightJourneyId[item.flights[0].journeyId]
    : undefined
  const returning = item.flights[1]
    ? journeysByFlightJourneyId[item.flights[1].journeyId]
    : undefined

  if (isReturnFares) {
    price = returning ? fares : (departing?.price.all.totalRoundTripPrice ?? 0)
  } else if (departing && !returning && item.viewType !== FlightViewTypes.ONE_WAY) {
    price = departing.price.all.totalFare + (item.quotedFare ?? 0) / 2
  }

  return price
}

/** Cost sent to POST /cart/evaluation — matches checkout item view totals. */
export function getFlightItemPolicyEvaluationCost(
  item: App.Checkout.FlightItem,
  journeysByFlightJourneyId: JourneysByFlightJourneyId,
): number {
  return getFlightItemViewPrice(item, journeysByFlightJourneyId) + sumOtherFees(item.otherFees)
}

export function getFlightItemPolicyEvaluationCostFromView(
  flightItemView: App.Checkout.FlightItemView,
): number {
  return flightItemView.totals.price + sumOtherFees(flightItemView.totals.otherFees)
}

export function buildJourneysByFlightJourneyId(
  item: App.Checkout.FlightItem,
  journeys: Array<App.JourneyV2>,
): JourneysByFlightJourneyId {
  return Object.fromEntries(
    item.flights.map((flight, index) => [flight.journeyId, journeys[index]]),
  )
}

function getFlightBaggageCost(
  item: App.Checkout.FlightItem,
  journeys: Array<App.JourneyV2>,
): number {
  return sum(
    journeys.map((journey, index) => {
      const baggageOptions = baggageOptionsMap(journey.flightGroup.flights[0])?.baggage ?? []
      return computeBaggageCostForPaxList(
        item.passengers,
        baggageOptions,
        item.flights[index]?.extras.baggage ?? {},
      )
    }),
  )
}

/** journey.cost for bundled return flight orders — policy cost plus selected baggage. */
export function getFlightOrderJourneyCost(
  item: App.Checkout.FlightItem,
  journeys: Array<App.JourneyV2>,
): number {
  const journeysByFlightJourneyId = buildJourneysByFlightJourneyId(item, journeys)
  return getFlightItemPolicyEvaluationCost(item, journeysByFlightJourneyId) + getFlightBaggageCost(item, journeys)
}
